import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import 'react-toastify/dist/ReactToastify.css';
import { showToast } from '@/common/showToast';
import { fetchTableByIdRest, updateTableRest } from '@/services/table/table-service';
import { blindStructuresRest, getGamesRest } from '@/services/game-params/parameters-services';
import { useQuery } from '@tanstack/react-query';
import { TableType } from '@/common/types/phenomTypes';
import { ComputedGameParams, PokerVariant, RakeStructure } from '@repo/common-types';
import { maxPlayersOptions } from '@/common/forms/options/MaxPlayerOptions';
import { useNavigate, useParams } from 'react-router-dom';
import routes from '@/routes/routes-enums';
import FormFields from '@/common/forms/FormFields';
import FormField from '../../template/form-field';
import { TemplateDto } from '@/services/template/template-types';
import { timeOutOptions } from '@/common/forms/options/TimeOutOptions';
import { fetchAllTemplatesRest } from '@/services/template/templateRest';
import { MixedTableDto } from '@/services/table/table-types';
import InfoField from '@/common/forms/InfoField';
import TableGameParamEdit from './components/TableGameParamsEdit';

const TableUpdate = () => {
  const navigate = useNavigate();
  const { tableId } = useParams<{ tableId: string }>();

  const { data: games, error: _gamesError } = useQuery({
    queryKey: ['getGames'],
    queryFn: () => getGamesRest(),
  });

  const [_templates, setTemplates] = useState<TemplateDto[]>([]);
  const [gameVariantTokens, setGameVariantTokens] = useState<string[]>([]);
  const [gameParams, setGameParams] = useState<Record<string, ComputedGameParams>>({});
  const [_gameParamErrors, setGameParamErrors] = useState<Record<string, string>>({});
  const [submission, setSubmission] = useState(false);
  const [blindStructures, setBlindStructures] = useState<RakeStructure[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const tableGameParamRefs = useRef<Record<string, React.RefObject<{ validate: () => Promise<boolean> } | null>>>({});

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Table Name is required'),
    game: Yup.string().required('Game Name is required'),
    variants: Yup.array()
      .of(Yup.mixed().oneOf(Object.values(PokerVariant), 'Invalid variant'))
      .required('Variants are required'),
    minBuyin: Yup.number().min(0, 'Min Buyin must be non-negative').required('Min Buyin is required'),
    maxBuyin: Yup.number()
      .min(0, 'Max Buyin must be non-negative')
      .required('Max Buyin is required')
      .moreThan(Yup.ref('minBuyin'), 'Max Buyin should be greater than Min Buyin'),
    maxPlayers: Yup.number()
      .min(2, 'Max Players must be at least 2')
      .max(9, 'Max Players must be at most 9')
      .required('Max Players is required'),
    timeoutInSeconds: Yup.number().required('Time to act is required'),
    isPublic: Yup.boolean(),
    templateId: Yup.number().notRequired(),
  });

  type ValidationSchema = Yup.InferType<typeof validationSchema>;

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<ValidationSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      game: '',
      variants: [],
      minBuyin: 0,
      maxBuyin: 0,
      maxPlayers: 6,
      timeoutInSeconds: 30,
      isPublic: false,
      templateId: undefined,
    },
  });

  useEffect(() => {
    if (!tableId) {
      showToast('Invalid table ID', 'warning');
      return;
    }

    const fetchTableData = async () => {
      try {
        const tableData = await fetchTableByIdRest(tableId);
        reset({
          name: tableData.name,
          game: tableData.game,
          variants: tableData.variants,
          minBuyin: tableData.minBuyin ?? undefined,
          maxBuyin: tableData.maxBuyin ?? undefined,
          maxPlayers: tableData.maxPlayers,
          timeoutInSeconds: tableData.timeoutInSeconds,
          isPublic: tableData.isPublic,
          templateId: tableData.tableTemplateId,
        });

        tableData.gameParams = tableData.gameParams.sort((a: any, b: any) => a.index - b.index);

        // Create tokens using the game params
        const tokens = tableData.gameParams.map(
          (param: ComputedGameParams, index: number) => `${tableData.game}+${param.variantId}#${index}`,
        );

        setGameVariantTokens(tokens);

        // Create gameParams object using the new tokens
        const paramsObject = tableData.gameParams.reduce(
          (acc: Record<string, ComputedGameParams>, param: ComputedGameParams, index: number) => {
            const token = tokens[index];
            acc[token] = param;
            return acc;
          },
          {},
        );

        setGameParams(paramsObject);
        setIsDataLoaded(true);
      } catch (_error) {
        showToast('Failed to fetch table data.', 'warning');
      }
    };

    fetchTableData();
    blindStructuresRest()
      .then(setBlindStructures)
      .catch(() => showToast('Failed to fetch rake structures.', 'warning'));
    fetchAllTemplatesRest()
      .then((response) => setTemplates(response.filter((template: TemplateDto) => template.variants?.length === 1)))
      .catch(() => showToast('Failed to fetch templates.', 'warning'));
  }, [tableId, reset]);

  useEffect(() => {
    gameVariantTokens.forEach((token) => {
      if (!tableGameParamRefs.current[token]) {
        tableGameParamRefs.current[token] = React.createRef();
      }
    });
  }, [gameVariantTokens]);

  const handleGameVariant = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (e.target.value === '' || !games) return;

      const gameId = e.target.value;
      const gameVariant = games.find((game) => game.id === gameId);

      if (!gameVariant) return;

      const variants = gameVariant.variants as PokerVariant[];
      const tokens = variants.map((variant, index) => `${gameId}+${variant}#${index}`);

      setGameVariantTokens(tokens);
      setValue('variants', variants);
      setValue('game', gameVariant.name);
      setGameParams({});
      setGameParamErrors({});
    },
    [games, setValue],
  );

  const handleValidGameParam = useCallback((token: string, config: ComputedGameParams) => {
    setGameParams((prev) => ({
      ...prev,
      [token]: config,
    }));
    setGameParamErrors((prev) => {
      const newErrors = { ...prev };

      delete newErrors[token];
      return newErrors;
    });
  }, []);

  const handleInvalidGameParam = useCallback((token: string) => {
    const [_gameId, variantInfo] = token.split('+');
    const [variant, index] = variantInfo.split('#');
    showToast(`Invalid game configuration on variant #${parseInt(index) + 1}, ${variant}`, 'warning');
  }, []);

  const validateGameParams = async () => {
    if (!isDataLoaded) return true;

    const newErrors: Record<string, string> = {};
    let isValid = true;

    for (const token of gameVariantTokens) {
      const ref = tableGameParamRefs.current[token];
      if (ref && ref.current) {
        const isParamValid = await ref.current.validate();
        if (!isParamValid) {
          const [_gameId, variantInfo] = token.split('+');
          const [variant, index] = variantInfo.split('#');
          newErrors[token] = `Invalid configuration for ${variant} (Variant #${parseInt(index) + 1})`;
          isValid = false;
        }
      } else {
        console.log(`No ref or current for ${token}`);
        newErrors[token] = `Game configuration is required for game ${token}`;
        isValid = false;
      }
    }

    setGameParamErrors(newErrors);

    if (!isValid) {
      const errorMessages = Object.values(newErrors).join(', ');
      showToast(`Please correct the following errors: ${errorMessages}`, 'warning');
    }

    return isValid;
  };

  const onSubmit = async (formData: ValidationSchema) => {
    const isGameParamsValid = await validateGameParams();
    if (!isGameParamsValid) {
      return;
    }

    setSubmission(true);

    try {
      const parsedGameParams: ComputedGameParams[] = Object.values(gameParams).map((config) => ({
        index: config.index,
        name: config.name,
        variantId: config.variantId,
        bringIn: parseToIntOrDefault(config?.bringIn, undefined),
        ante: parseToIntOrDefault(config?.ante, 0),
        bbAnte: parseToIntOrDefault(config?.bbAnte, 0),
        sbAnte: parseToIntOrDefault(config?.sbAnte, 0),
        smallBet: parseToIntOrDefault(config?.smallBet, 0),
        bigBet: parseToIntOrDefault(config?.bigBet, 0),
        bettingCap: parseToIntOrDefault(config?.bettingCap, 0),
        smallBlind: parseToIntOrDefault(config?.smallBlind, 0),
        bigBlind: parseToIntOrDefault(config?.bigBlind, 0),
      }));

      const firstGame = parsedGameParams[0];

      const tableData: MixedTableDto = {
        id: tableId!,
        type: TableType.CASH,
        gameParams: parsedGameParams,
        isPublic: formData.isPublic ?? false,
        timeoutInSeconds: parseInt(formData.timeoutInSeconds as any, 10),
        minBuyin: parseFloat(formData.minBuyin as any),
        maxBuyin: parseFloat(formData.maxBuyin as any),
        smallBlind: parseToFloatToZero(String(firstGame.smallBlind)),
        bigBlind: parseToFloatToZero(String(firstGame.bigBlind)),
        smallBet: parseToFloatToZero(String(firstGame.smallBet)),
        bigBet: parseToFloatToZero(String(firstGame.bigBet)),
        maxPlayers: parseInt(formData.maxPlayers as any, 10),
        game: formData.game,
        name: formData.name,
        templateId: undefined,
        handsPerGame: 6,
      };

      const response = await updateTableRest(tableData);
      showToast('Table updated successfully', 'success');
      navigate(`${routes.TABLE}/${String(response.data.id)}`);
    } catch (error: any) {
      setSubmission(false);
      handleServerError(error);
    }
  };

  const handleServerError = (error: any) => {
    const handleErrors = error.response?.data?.message;

    if (!handleErrors) {
      showToast('Unknown error from server', 'warning');
      return;
    }

    const errors = Array.isArray(handleErrors) ? handleErrors : [handleErrors];

    errors.forEach((error: string) => {
      showToast(error, 'warning');
    });
  };

  const onError = (errors: FieldErrors<ValidationSchema>) => {
    const errorMessages = Object.entries(errors)
      .map(([field, error]) => (error?.message ? `${field}: ${error.message}` : null))
      .filter((message): message is string => typeof message === 'string')
      .join(', ');
    showToast(`Please review provided fields: ${errorMessages}`, 'warning');
  };

  function parseToFloatToZero(initialValue: any): number | undefined {
    if (initialValue === undefined) return undefined;
    if (initialValue === '0') return 0;
    const parsedValue = parseFloat(String(initialValue));
    return isNaN(parsedValue) ? 0 : parsedValue;
  }

  function parseToIntOrDefault(initialValue: any, defaultValue: number | undefined): number | undefined {
    if (initialValue === undefined) return defaultValue;
    if (initialValue === '0') return 0;
    const parsedValue = parseFloat(String(initialValue));
    const fault = parseFloat(String(defaultValue));
    return isNaN(parsedValue) ? (isNaN(fault) ? fault : undefined) : parsedValue;
  }

  return (
    <React.Fragment>
      <div className="page-content mb-6">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={12}>
              <Card className="card mb-2">
                <CardBody className="card-body">
                  <h3 className="mb-5">Update Table</h3>
                  <form onSubmit={handleSubmit(onSubmit, onError)}>
                    <FormFields>
                      <FormField name="name" control={control} label="Table Name" error={errors.name?.message} />
                      <FormField name="game" control={control} label="Game Name" error={errors.game?.message} />
                    </FormFields>

                    <FormFields>
                      <Col lg={5} sm={12} className={`mb-4`}>
                        <label htmlFor="variants">Select a Game</label>
                        <div className="input-group">
                          <select
                            onChange={handleGameVariant}
                            className={`form-select ${errors.variants ? 'is-invalid' : ''}`}
                            value={watch('game')}
                          >
                            <option value="">Select game</option>
                            {games &&
                              games.map((game) => (
                                <option key={game.id} value={game.id}>
                                  {game.name}
                                </option>
                              ))}
                          </select>
                        </div>
                        {errors.variants && <span className="invalid-feedback">{errors.variants.message}</span>}
                      </Col>
                      {watch('variants')?.length > 0 && (
                        <Col lg={5} sm={12} className={`mb-4`}>
                          <div className="mb-3">
                            <b>Variants</b>
                          </div>
                          <ul>
                            {(watch('variants') as PokerVariant[]).map((variant: PokerVariant) => (
                              <li key={variant}>{variant.replace(/([a-z])([A-Z])/g, '$1 $2')}</li>
                            ))}
                          </ul>
                        </Col>
                      )}
                    </FormFields>

                    <FormFields>
                      <InfoField value={control._formValues?.minBuyin} name="Min Buyin" prefix="$" />
                      <InfoField value={control._formValues?.maxBuyin} name="Max Buyin" prefix="$" />
                    </FormFields>
                    <FormFields>
                      <FormField
                        type="select"
                        name="maxPlayers"
                        control={control}
                        label="Table Size"
                        error={errors.maxPlayers?.message}
                        options={maxPlayersOptions()}
                      />
                      <FormField
                        type="select"
                        name="timeoutInSeconds"
                        control={control}
                        label="Time to act"
                        error={errors.timeoutInSeconds?.message}
                        options={timeOutOptions()}
                      />
                    </FormFields>
                    <FormFields>
                      <div className="form-check form-switch form-switch-right form-switch-md d-flex ms-2 align-bottom">
                        <div className="d-flex">
                          <span>Is Public</span>
                          <Controller
                            name="isPublic"
                            control={control}
                            render={({ field }) => (
                              <input
                                className="form-check-input code-switcher ms-4"
                                type="checkbox"
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </FormFields>

                    {isDataLoaded && gameVariantTokens.length > 0 && (
                      <div className="mt-4">
                        {gameVariantTokens.map((token) => {
                          const [_gameId, variantInfo] = token.split('+');
                          const [variant, _index] = variantInfo.split('#');
                          return (
                            <div key={token}>
                              <TableGameParamEdit
                                key={token}
                                ref={tableGameParamRefs.current[token]}
                                blindStructures={blindStructures}
                                mixedGame={gameVariantTokens.length > 1}
                                variant={variant as PokerVariant}
                                initialValues={gameParams[token]}
                                onValidConfig={(config) => handleValidGameParam(token, config)}
                                onInvalidConfig={() => handleInvalidGameParam(token)}
                                isDataLoaded={isDataLoaded}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Col lg={10} className="mt-5">
                      <div className="d-flex justify-content-end mt-4 flex-row">
                        <Button type="submit" className="my-4 ms-5" color="primary" disabled={submission}>
                          {submission && <Spinner size={'sm'} className="mx-2" />} Update Table
                        </Button>
                      </div>
                    </Col>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TableUpdate;
